import {
    Box,
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { ComponentProps } from 'react';
import { Alert } from '@acp/core-ui';
import { PropertyAddressCard } from '../property-address-card';
import { useProperty, usePropertyKeyPickupLocation } from '../../hooks/use-property';
import { KeyOnsiteConfirmation } from './key-onsite-confirmation';
import { EditIntegratedPropertyFormValues } from './integrated-form-schema';

export function ConfirmIntegratedPropertyEditScreen({
    propertyId,
    onBack,
    onSubmit,
    error,
    showHeader = true,
    p,
    confirmButtonText,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onSubmit: (vals: EditIntegratedPropertyFormValues) => void;
    onBack: () => void;
    error: Error | null;
    showHeader?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
}) {
    const {
        handleSubmit,
    } = useFormContext<EditIntegratedPropertyFormValues>();

    const propertyPickupLocation = usePropertyKeyPickupLocation(propertyId);

    const { data, isLoading } = useProperty(propertyId);

    return (
        <VStack align="left">
            { showHeader && (<ModalHeader>Confirm property update</ModalHeader>)}

            <ModalBody p={p}>
                { showHeader && (<PropertyAddressCard w="full" mb={5} propertyId={propertyId} />)}

                {showAgencyKeyFields && (
                    <Box w="full">
                        <VStack gap={6} pb={4}>
                            <Text>
                                Please confirm that all the information you have entered for the
                                property is accurate before proceeding with the submission.
                            </Text>

                            <Box w="full">
                                <Text fontWeight="medium" fontSize="md">
                                    Property manager
                                </Text>
                                <Text>
                                    {data?.propertyManager?.name}
                                </Text>
                            </Box>

                            <Box w="full">
                                <Text fontWeight="medium" fontSize="md">
                                    Key number
                                </Text>
                                <Text>
                                    {data?.keyNumber}
                                </Text>
                            </Box>

                            {propertyPickupLocation.data !== undefined && (
                                <Box w="full">
                                    <Text fontWeight="medium" fontSize="md">
                                        Key pickup location
                                    </Text>

                                    {data?.isKeyAvailable !== undefined
                                    && data?.isKeyAvailable
                                        ? <Text>This key is not available to be picked up</Text>
                                        : (
                                            <Text>
                                                {propertyPickupLocation.data.fullAddress}
                                            </Text>
                                        )}
                                </Box>
                            )}
                        </VStack>
                    </Box>
                )}

                {showOnsiteKeyFields && (
                    <KeyOnsiteConfirmation propertyId={propertyId} />
                )}
                {error !== null && (
                    <Alert status="error" title="An error occurred while submitting your request. Please try again." mt={5}>
                        {error.message}
                    </Alert>
                )}
            </ModalBody>

            <ModalFooter p={p} justifyContent="space-between">
                <Button
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                    onClick={onBack}
                >
                    Back
                </Button>
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                >
                    {confirmButtonText ?? 'Update property'}
                </Button>
            </ModalFooter>
        </VStack>
    );
}
